import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import axios from "axios"
import {
  Plus,
  Download,
  FileText,
  PenLine,
  Settings2,
  Trash2,
  ExternalLink,
  Search,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import AppLayout from "@/components/app/AppLayout"
import { FeatureGate } from "@/components/app/FeatureGate"
import { PilotBanner } from "@/components/app/PilotBanner"
import { CarePlanFormRenderer } from "@/components/care-plan/CarePlanFormRenderer"
import { SignaturePad } from "@/components/app/SignaturePad"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { API_BASE } from "@/config/api"
import { modalitiesForVerticals } from "@/config/clinical-verticals"
import { useClinicalContext } from "@/hooks/use-clinical-context"
import { formatDate, formatDateTime } from "@/lib/format"
import { toast } from "sonner"

const API = API_BASE
const STATUSES = ["draft", "signed", "active"]
const XML_MODALITIES = ["physio", "psych", "kineto", "speech", "logopedie", "pis", "home_care"]

function statusBadgeVariant(status) {
  if (status === "signed") return "default"
  if (status === "active") return "outline"
  return "secondary"
}

export default function CarePlans() {
  const { t } = useTranslation()
  const { enabledVerticals, effectiveVertical, setWorkingVertical } = useClinicalContext()
  const modalities = modalitiesForVerticals(enabledVerticals)
  const defaultModality = modalities[0]?.value || "physio"

  function modalityForVertical(vertical) {
    if (!vertical) return defaultModality
    if (vertical === "home_care") return modalities.some((m) => m.value === "pis") ? "pis" : defaultModality
    return modalities.some((m) => m.value === vertical) ? vertical : defaultModality
  }

  function verticalForModality(modality) {
    if (modality === "pis") return "home_care"
    return modality
  }
  const [params, setParams] = useSearchParams()
  const queryHandled = useRef("")
  const planHandled = useRef("")

  const [clients, setClients] = useState([])
  const [plans, setPlans] = useState([])
  const [summary, setSummary] = useState({})
  const [loading, setLoading] = useState(true)
  const [filterStatus, setFilterStatus] = useState("")
  const [filterClient, setFilterClient] = useState(params.get("client") || "")
  const [filterModality, setFilterModality] = useState("")
  const [composeModality, setComposeModality] = useState(defaultModality)
  const [previewTemplate, setPreviewTemplate] = useState(null)
  const [modalityTemplates, setModalityTemplates] = useState([])
  const [editorTemplates, setEditorTemplates] = useState([])
  const [selectedPreviewTemplateId, setSelectedPreviewTemplateId] = useState("")
  const [previewLoading, setPreviewLoading] = useState(false)
  const [composeTitle, setComposeTitle] = useState("")
  const [searchQ, setSearchQ] = useState("")

  const [selected, setSelected] = useState(null)
  const [templateDef, setTemplateDef] = useState(null)
  const [fieldValues, setFieldValues] = useState({})
  const [title, setTitle] = useState("")
  const [modality, setModality] = useState("physio")
  const [status, setStatus] = useState("draft")
  const [addOpen, setAddOpen] = useState(false)
  const [signOpen, setSignOpen] = useState(false)
  const [signatureData, setSignatureData] = useState(null)
  const [hrAnnexOpen, setHrAnnexOpen] = useState(false)
  const [hrEmployees, setHrEmployees] = useState([])
  const [hrEmployeeId, setHrEmployeeId] = useState("")
  const [hrAnnexLoading, setHrAnnexLoading] = useState(false)
  const [newForm, setNewForm] = useState({
    client_id: "",
    title: "",
    modality: defaultModality,
    template_id: "",
  })
  const [templates, setTemplates] = useState([])
  const [speech, setSpeech] = useState([])
  const [spForm, setSpForm] = useState({ metric: "", value: "", notes: "" })

  const modalityLabel = useCallback(
    (m) => modalities.find((x) => x.value === m)?.label || m,
    [modalities]
  )

  const loadPlans = useCallback(async () => {
    setLoading(true)
    try {
      const q = {}
      if (filterStatus) q.status = filterStatus
      if (filterClient) q.client_id = filterClient
      if (filterModality) q.modality = filterModality
      const { data } = await axios.get(`${API}/care-plans`, { params: q })
      if (Array.isArray(data)) {
        setPlans(data)
        setSummary({ total: data.length })
      } else {
        setPlans(data.care_plans || [])
        setSummary(data.summary || {})
      }
    } catch {
      toast.error(t("app.carePlans.toast.loadFailed"))
    } finally {
      setLoading(false)
    }
  }, [filterStatus, filterClient, filterModality, t])

  const loadTemplates = useCallback((mod) => {
    return axios
      .get(`${API}/care-plan-templates`, { params: mod ? { modality: mod } : {} })
      .then((r) => {
        const list = r.data || []
        setTemplates(list)
        return list
      })
      .catch(() => [])
  }, [])

  const sortTemplates = (list) =>
    [...list].sort((a, b) => {
      if (a.is_system !== b.is_system) return a.is_system ? -1 : 1
      return (a.sort_order || 0) - (b.sort_order || 0) || (a.name || "").localeCompare(b.name || "")
    })

  const loadPreviewForModality = useCallback(
    async (mod, keepTemplateId) => {
      if (!mod) {
        setPreviewTemplate(null)
        setModalityTemplates([])
        setSelectedPreviewTemplateId("")
        return
      }
      setPreviewLoading(true)
      try {
        const list = sortTemplates((await loadTemplates(mod)).filter((x) => x.modality === mod))
        setModalityTemplates(list)
        const tpl =
          list.find((x) => x.id === keepTemplateId) ||
          list.find((x) => x.is_system) ||
          list[0] ||
          null
        setSelectedPreviewTemplateId(tpl?.id || "")
        setPreviewTemplate(tpl)
      } catch {
        setPreviewTemplate(null)
        setModalityTemplates([])
        toast.error(t("app.carePlans.toast.loadFailed"))
      } finally {
        setPreviewLoading(false)
      }
    },
    [loadTemplates, t]
  )

  const onPreviewTemplateChange = (templateId) => {
    setSelectedPreviewTemplateId(templateId)
    const tpl = modalityTemplates.find((x) => x.id === templateId) || null
    setPreviewTemplate(tpl)
  }

  useEffect(() => {
    axios.get(`${API}/clients`).then((r) => setClients(r.data || [])).catch(() => {})
    loadPlans()
    loadTemplates()
  }, [loadPlans, loadTemplates])

  useEffect(() => {
    if (!effectiveVertical) return
    setComposeModality(modalityForVertical(effectiveVertical))
  }, [effectiveVertical, defaultModality, modalities])

  useEffect(() => {
    if (filterModality) setComposeModality(filterModality)
  }, [filterModality])

  useEffect(() => {
    if (!selected) loadPreviewForModality(composeModality)
  }, [selected, composeModality, loadPreviewForModality])

  useEffect(() => {
    if (!selected) {
      setEditorTemplates([])
      return
    }
    const mod = selected.modality || modality
    loadTemplates(mod)
      .then((list) => setEditorTemplates(sortTemplates(list.filter((x) => x.modality === mod))))
      .catch(() => setEditorTemplates([]))
  }, [selected?.id, selected?.modality, modality, loadTemplates])

  useEffect(() => {
    const clientId = params.get("client") || ""
    const action = params.get("action") || ""
    const key = `${clientId}|${action}`
    if (key === "|") return
    if (queryHandled.current === key) return
    queryHandled.current = key

    if (clientId) setFilterClient(clientId)

    if (action === "new") {
      setNewForm((f) => ({
        ...f,
        modality: composeModality,
        client_id: clientId || f.client_id,
      }))
      loadTemplates(composeModality)
      setAddOpen(true)
      const next = new URLSearchParams(params)
      next.delete("action")
      setParams(next, { replace: true })
    }
  }, [params, composeModality, loadTemplates, setParams])

  useEffect(() => {
    setNewForm((f) => ({ ...f, modality: defaultModality }))
  }, [defaultModality])

  const filterClientName = useMemo(() => {
    if (!filterClient) return ""
    return clients.find((c) => c.id === filterClient)?.name || ""
  }, [clients, filterClient])

  const clearClientFilter = () => {
    setFilterClient("")
    const next = new URLSearchParams(params)
    next.delete("client")
    next.delete("action")
    setParams(next, { replace: true })
    queryHandled.current = ""
  }

  const openComposeForFiltered = () => {
    setNewForm((f) => ({
      ...f,
      modality: composeModality,
      client_id: filterClient || f.client_id,
    }))
    loadTemplates(composeModality)
    setAddOpen(true)
  }

  const visiblePlans = useMemo(() => {
    const q = searchQ.trim().toLowerCase()
    if (!q) return plans
    return plans.filter(
      (p) =>
        (p.title || "").toLowerCase().includes(q) ||
        (p.client_name || "").toLowerCase().includes(q)
    )
  }, [plans, searchQ])

  const applyPlanFromApi = async (data) => {
    setSelected(data)
    setTitle(data.title || "")
    setModality(data.modality || "physio")
    setStatus(data.status || "draft")
    const body = data.body && typeof data.body === "object" ? data.body : {}
    setFieldValues(body.field_values || {})
    let defn = data.template?.definition || null
    const tplId = data.template_id || body.template_id
    if (!defn && tplId) {
      try {
        const tr = await axios.get(`${API}/care-plan-templates/${tplId}`)
        defn = tr.data.definition
      } catch {
        defn = null
      }
    }
    setTemplateDef(defn)
    if (["speech", "logopedie"].includes((data.modality || "").toLowerCase())) {
      axios
        .get(`${API}/clients/${data.client_id}/speech-progress`)
        .then((r) => setSpeech(r.data || []))
        .catch(() => setSpeech([]))
    } else {
      setSpeech([])
    }
  }

  const loadOne = async (id) => {
    try {
      const { data } = await axios.get(`${API}/care-plans/${id}`)
      await applyPlanFromApi(data)
    } catch {
      toast.error(t("app.carePlans.toast.unavailable"))
    }
  }

  useEffect(() => {
    const planId = params.get("plan") || ""
    if (!planId || loading) return
    if (planHandled.current === planId) return
    if (selected?.id === planId) {
      planHandled.current = planId
      return
    }
    planHandled.current = planId
    loadOne(planId)
    // loadOne is stable enough for deep-link; avoid re-fetch loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, loading, plans])

  const clearSelection = () => {
    setSelected(null)
    setTemplateDef(null)
    setFieldValues({})
  }

  const onFilterModalityChange = (v) => {
    const mod = v === "__all__" ? "" : v
    setFilterModality(mod)
    if (mod) {
      setComposeModality(mod)
      clearSelection()
    }
  }

  const onComposeModalityChange = (mod) => {
    setComposeModality(mod)
    setFilterModality(mod)
    setWorkingVertical(verticalForModality(mod))
    clearSelection()
    loadPreviewForModality(mod, "")
  }

  const createFromCompose = async () => {
    const clientId = filterClient || newForm.client_id
    if (!clientId || !composeTitle.trim()) {
      return toast.error(t("app.carePlans.toast.fillClientTitle"))
    }
    const client = clients.find((c) => c.id === clientId)
    if (!client) return toast.error(t("app.carePlans.toast.fillClientTitle"))
    const tpl =
      previewTemplate ||
      modalityTemplates.find((x) => x.id === selectedPreviewTemplateId) ||
      templates.find((x) => x.is_system && x.modality === composeModality)
    try {
      const { data } = await axios.post(`${API}/care-plans`, {
        client_id: clientId,
        title: composeTitle.trim(),
        modality: composeModality,
        template_id: tpl?.id || undefined,
        body: {},
      })
      toast.success(t("app.carePlans.toast.generated"))
      setComposeTitle("")
      await loadPlans()
      await applyPlanFromApi(data)
    } catch (e) {
      toast.error(e.response?.data?.detail || t("app.carePlans.toast.createFailed"))
    }
  }

  const changeTemplate = async (templateId) => {
    if (!selected || status === "signed") return
    const currentId = selected.template_id || selected.body?.template_id || ""
    if (templateId === currentId) return
    const ok = window.confirm(t("app.carePlans.templateChangeConfirm"))
    if (!ok) return
    try {
      const { data } = await axios.patch(`${API}/care-plans/${selected.id}`, { template_id: templateId })
      await applyPlanFromApi(data)
      toast.success(t("app.carePlans.toast.templateChanged"))
      loadPlans()
    } catch (e) {
      const msg = e.response?.data?.detail
      toast.error(typeof msg === "string" ? msg : t("app.carePlans.toast.saveFailed"))
    }
  }

  const changeModality = async (newModality) => {
    if (!selected || status === "signed") return
    if (newModality === selected.modality) {
      setModality(newModality)
      return
    }
    const ok = window.confirm(t("app.carePlans.modalityChangeConfirm"))
    if (!ok) {
      setModality(selected.modality)
      return
    }
    setModality(newModality)
    try {
      const { data } = await axios.patch(`${API}/care-plans/${selected.id}`, { modality: newModality })
      await applyPlanFromApi(data)
      toast.success(t("app.carePlans.toast.modalityChanged"))
      loadPlans()
    } catch (e) {
      setModality(selected.modality)
      const msg = e.response?.data?.detail
      toast.error(typeof msg === "string" ? msg : t("app.carePlans.toast.saveFailed"))
    }
  }

  const saveMeta = async () => {
    if (!selected) return
    try {
      const { data } = await axios.patch(`${API}/care-plans/${selected.id}`, { title })
      await applyPlanFromApi(data)
      toast.success(t("app.carePlans.toast.saved"))
      loadPlans()
    } catch {
      toast.error(t("app.carePlans.toast.saveFailed"))
    }
  }

  const saveBody = async () => {
    if (!selected || status === "signed") return
    try {
      await axios.patch(`${API}/care-plans/${selected.id}`, {
        body: {
          schema_version: 2,
          template_id: selected.template_id || selected.body?.template_id,
          field_values: fieldValues,
        },
      })
      toast.success(t("app.carePlans.toast.planSaved"))
      loadPlans()
    } catch (e) {
      const errs = e.response?.data?.errors
      toast.error(Array.isArray(errs) ? errs.join("; ") : t("app.carePlans.toast.saveFailed"))
    }
  }

  const createPlan = async () => {
    if (!newForm.client_id || !newForm.title.trim()) {
      return toast.error(t("app.carePlans.toast.fillClientTitle"))
    }
    try {
      const tpl = templates.find(
        (x) =>
          x.modality === newForm.modality &&
          (newForm.template_id ? x.id === newForm.template_id : x.is_system)
      )
      const { data } = await axios.post(`${API}/care-plans`, {
        client_id: newForm.client_id,
        title: newForm.title.trim(),
        modality: newForm.modality,
        template_id: tpl?.id || newForm.template_id || undefined,
        body: {},
      })
      toast.success(t("app.carePlans.toast.generated"))
      setAddOpen(false)
      setNewForm({ client_id: "", title: "", modality: defaultModality, template_id: "" })
      await loadPlans()
      await applyPlanFromApi(data)
    } catch (e) {
      toast.error(e.response?.data?.detail || t("app.carePlans.toast.createFailed"))
    }
  }

  const signPlan = async () => {
    if (!selected || !signatureData) return
    try {
      await axios.post(`${API}/care-plans/${selected.id}/sign`, { signature_data: signatureData })
      toast.success(t("app.carePlans.toast.signed"))
      setSignOpen(false)
      setSignatureData(null)
      loadOne(selected.id)
      loadPlans()
    } catch (e) {
      const errs = e.response?.data?.errors
      toast.error(Array.isArray(errs) ? errs.join("; ") : t("app.carePlans.toast.signFailed"))
    }
  }

  const deletePlan = async (id) => {
    try {
      await axios.delete(`${API}/care-plans/${id}`)
      if (selected?.id === id) clearSelection()
      toast.success(t("app.carePlans.toast.deleted"))
      loadPlans()
    } catch (e) {
      toast.error(e.response?.data?.detail || t("app.carePlans.toast.saveFailed"))
    }
  }

  const exportPlan = async (format, target = "patient") => {
    if (!selected) return
    try {
      const { data } = await axios.get(`${API}/care-plans/${selected.id}/export`, {
        params: { format, target },
        responseType: "blob",
      })
      const mime = format === "pdf" ? "application/pdf" : "application/xml"
      const ext = format === "pdf" ? "pdf" : "xml"
      const blob = new Blob([data], { type: mime })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `plan-${target}.${ext}`
      a.click()
      URL.revokeObjectURL(url)
      toast.success(t("app.carePlans.toast.downloaded"))
    } catch {
      toast.error(t("app.carePlans.toast.exportUnavailable"))
    }
  }

  const generateHrAnnex = async () => {
    if (!selected || !hrEmployeeId) return
    setHrAnnexLoading(true)
    try {
      const { data } = await axios.post(`${API}/care-plans/${selected.id}/hr-annex`, {
        employee_id: hrEmployeeId,
      })
      toast.success(
        t("app.carePlans.toast.hrAnnexCreated", { id: data.contract_id?.slice(0, 8) || "—" })
      )
      setHrAnnexOpen(false)
      setHrEmployeeId("")
    } catch (e) {
      toast.error(e.response?.data?.detail || t("app.carePlans.toast.hrAnnexFailed"))
    } finally {
      setHrAnnexLoading(false)
    }
  }

  const openHrAnnexDialog = () => {
    if (!selected) return
    setHrEmployeeId("")
    setHrAnnexOpen(true)
    axios
      .get(`${API}/hr/employees`, { params: { status: "active" } })
      .then((r) => {
        const list = (r.data || []).filter((e) => !e.role_code || e.role_code === "app")
        setHrEmployees(list.length ? list : r.data || [])
      })
      .catch(() => setHrEmployees([]))
  }

  const addSpeechProgress = async () => {
    if (!selected || !spForm.metric.trim()) return
    try {
      await axios.post(`${API}/clients/${selected.client_id}/speech-progress`, spForm)
      setSpForm({ metric: "", value: "", notes: "" })
      const { data } = await axios.get(`${API}/clients/${selected.client_id}/speech-progress`)
      setSpeech(data || [])
      toast.success(t("app.carePlans.toast.entryAdded"))
    } catch {
      toast.error(t("app.carePlans.toast.error"))
    }
  }

  const downloadSpeech = async (format) => {
    if (!selected) return
    try {
      const { data } = await axios.get(`${API}/clients/${selected.client_id}/speech-report`, {
        params: { format },
        responseType: "blob",
      })
      const mime = format === "pdf" ? "application/pdf" : "text/html"
      const blob = new Blob([data], { type: mime })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = format === "pdf" ? "raport-logopedie.pdf" : "raport-logopedie.html"
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.error(t("app.carePlans.toast.reportUnavailable"))
    }
  }

  const isSpeech = ["speech", "logopedie"].includes((modality || "").toLowerCase())
  const isPis = ["pis", "home_care"].includes((modality || "").toLowerCase())
  const isSigned = status === "signed"
  const showXmlPilot = XML_MODALITIES.includes((modality || "").toLowerCase())
  const tplOptions = templates.filter((x) => x.modality === newForm.modality)
  const statusLabel = (s) => t(`app.carePlans.status.${s}`, { defaultValue: s })

  return (
    <FeatureGate
      feature="care_plans"
      title={t("app.carePlans.gateTitle")}
      description={t("app.carePlans.gateDescription")}
    >
      <AppLayout
        title={t("app.carePlans.title")}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className="rounded-full" asChild>
              <Link to="/app/care-plan-templates">
                <Settings2 className="h-4 w-4 mr-1" />
                {t("app.carePlans.templatesLink")}
              </Link>
            </Button>
            <Button
              size="sm"
              className="rounded-full"
              onClick={() => {
                setNewForm((f) => ({
                  ...f,
                  modality: composeModality,
                  client_id: filterClient || f.client_id,
                }))
                loadTemplates(composeModality)
                setAddOpen(true)
              }}
            >
              <Plus strokeWidth={1.5} className="h-4 w-4 mr-1" />
              {t("app.carePlans.generateNew")}
            </Button>
          </div>
        }
      >
        <p className="text-xs text-muted-foreground mb-4 max-w-2xl">
          {t("app.carePlans.intro")}{" "}
          <Link to="/app/clients" className="underline underline-offset-2">
            {t("app.nav.clients")}
          </Link>
          {" · "}
          <Link to="/app/notes" className="underline underline-offset-2">
            {t("app.nav.notes")}
          </Link>
          {" · "}
          <Link to="/app/care-plan-templates" className="underline underline-offset-2">
            {t("app.carePlans.templatesLink")}
          </Link>
        </p>

        {filterClient && filterClientName && (
          <div className="mb-3">
            <span className="inline-flex items-center gap-2 text-xs px-2.5 py-1 rounded-full border border-border bg-muted/40">
              {t("app.carePlans.filteredBy", { name: filterClientName })}
              <button type="button" className="text-muted-foreground hover:text-foreground" onClick={clearClientFilter}>
                ×
              </button>
            </span>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-3">
          <Button
            size="sm"
            variant={filterStatus === "" ? "default" : "outline"}
            className="rounded-full h-8"
            onClick={() => setFilterStatus("")}
          >
            {t("app.carePlans.filterAll")} ({summary.total || 0})
          </Button>
          {STATUSES.map((s) => (
            <Button
              key={s}
              size="sm"
              variant={filterStatus === s ? "default" : "outline"}
              className="rounded-full h-8 capitalize"
              onClick={() => setFilterStatus(s)}
            >
              {statusLabel(s)} ({summary[s] || 0})
            </Button>
          ))}
        </div>

        <div className="grid lg:grid-cols-5 gap-6 max-w-6xl">
          <aside className="lg:col-span-2 space-y-3">
            <div className="rounded-xl border border-border bg-card p-3 space-y-3">
              <h3 className="text-sm font-medium">{t("app.carePlans.listTitle")}</h3>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  className="pl-8 h-8 text-sm"
                  placeholder={t("app.carePlans.searchPlaceholder")}
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-1 gap-2">
                <div>
                  <Label className="text-[10px] text-muted-foreground">{t("app.carePlans.filterClient")}</Label>
                  <Select
                    value={filterClient || "__all__"}
                    onValueChange={(v) => {
                      if (v === "__all__") clearClientFilter()
                      else setFilterClient(v)
                    }}
                  >
                    <SelectTrigger className="mt-0.5 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">{t("app.carePlans.filterClientAll")}</SelectItem>
                      {clients.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">{t("app.carePlans.filterModality")}</Label>
                  <Select
                    value={filterModality || "__all__"}
                    onValueChange={onFilterModalityChange}
                  >
                    <SelectTrigger className="mt-0.5 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">{t("app.carePlans.filterModalityAll")}</SelectItem>
                      {modalities.map((m) => (
                        <SelectItem key={m.value} value={m.value}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <ul className="rounded-xl border border-border divide-y bg-card text-sm max-h-[52vh] overflow-auto">
              {loading && (
                <li className="px-4 py-8 text-center text-muted-foreground text-xs">
                  {t("app.carePlans.loading")}
                </li>
              )}
              {!loading &&
                visiblePlans.map((p) => (
                  <li key={p.id} className="group">
                    <div
                      className={`flex items-start gap-2 px-3 py-2.5 ${
                        selected?.id === p.id ? "bg-primary/10" : "hover:bg-muted/50"
                      }`}
                    >
                      <button
                        type="button"
                        className="flex-1 min-w-0 text-left"
                        onClick={() => loadOne(p.id)}
                      >
                        <p className="font-medium truncate text-[13px]">{p.title}</p>
                        {p.client_name && (
                          <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                            {p.client_name}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          <Badge variant="outline" className="text-[10px] h-5 font-normal">
                            {modalityLabel(p.modality)}
                          </Badge>
                          <Badge variant={statusBadgeVariant(p.status)} className="text-[10px] h-5 capitalize">
                            {statusLabel(p.status || "draft")}
                          </Badge>
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {formatDate(p.updated_at)}
                        </p>
                      </button>
                      {(p.status || "draft") === "draft" && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>{t("app.carePlans.deleteTitle")}</AlertDialogTitle>
                              <AlertDialogDescription>{t("app.carePlans.deleteBody")}</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t("app.carePlans.cancel")}</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deletePlan(p.id)}>
                                {t("app.carePlans.confirmDelete")}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </li>
                ))}
              {!loading && !visiblePlans.length && (
                <li className="px-4 py-8 text-center text-muted-foreground text-xs space-y-3">
                  {filterClient ? (
                    <>
                      <p>{t("app.carePlans.emptyFiltered")}</p>
                      <Button size="sm" className="rounded-full h-8" onClick={openComposeForFiltered}>
                        <Plus strokeWidth={1.5} className="h-3.5 w-3.5 mr-1" />
                        {t("app.carePlans.newPlanForClient")}
                      </Button>
                    </>
                  ) : (
                    <p>{t("app.carePlans.emptyList")}</p>
                  )}
                </li>
              )}
            </ul>
          </aside>

          <section className="lg:col-span-3 space-y-4 min-w-0">
            {!selected && (
              <div className="space-y-4">
                <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                  <p className="text-sm text-muted-foreground">{t("app.carePlans.composeHint")}</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">{t("app.carePlans.composeModality")}</Label>
                      <Select value={composeModality} onValueChange={onComposeModalityChange}>
                        <SelectTrigger className="mt-1 h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {modalities.map((m) => (
                            <SelectItem key={m.value} value={m.value}>
                              {m.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">{t("app.carePlans.filterClient")}</Label>
                      <Select
                        value={filterClient || "__none__"}
                        onValueChange={(v) => setFilterClient(v === "__none__" ? "" : v)}
                      >
                        <SelectTrigger className="mt-1 h-9">
                          <SelectValue placeholder={t("app.carePlans.selectClient")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">{t("app.carePlans.selectClient")}</SelectItem>
                          {clients.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {modalityTemplates.length > 0 && (
                    <div>
                      <Label className="text-xs">{t("app.carePlans.selectTemplate")}</Label>
                      <Select
                        value={selectedPreviewTemplateId || modalityTemplates[0]?.id || ""}
                        onValueChange={onPreviewTemplateChange}
                      >
                        <SelectTrigger className="mt-1 h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          {modalityTemplates.map((tpl) => (
                            <SelectItem key={tpl.id} value={tpl.id}>
                              {tpl.name}
                              {!tpl.is_system ? " (cabinet)" : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {modalityTemplates.length} {t("app.carePlans.templatesAvailable")}
                      </p>
                    </div>
                  )}
                  {previewTemplate?.name && (
                    <div>
                      <p className="text-xs font-medium">{t("app.carePlans.templatePreview")}</p>
                      <p className="text-sm mt-0.5">{previewTemplate.name}</p>
                      {(previewTemplate.definition?.legal_basis || []).length > 0 && (
                        <p className="text-[11px] text-muted-foreground mt-1">
                          {t("app.carePlans.templateLegalBasis")}:{" "}
                          {(previewTemplate.definition.legal_basis || []).join(" · ")}
                        </p>
                      )}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Input
                      className="flex-1 min-w-[200px] h-9"
                      placeholder={t("app.carePlans.quickTitlePlaceholder")}
                      value={composeTitle}
                      onChange={(e) => setComposeTitle(e.target.value)}
                    />
                    <Button
                      type="button"
                      className="rounded-full"
                      onClick={createFromCompose}
                      disabled={previewLoading || !previewTemplate}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      {t("app.carePlans.createFromTemplate")}
                    </Button>
                  </div>
                </div>

                {previewLoading && (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    {t("app.carePlans.previewLoading")}
                  </p>
                )}
                {!previewLoading && previewTemplate?.definition && (
                  <CarePlanFormRenderer
                    key={`preview-${composeModality}-${previewTemplate.id}`}
                    definition={previewTemplate.definition}
                    fieldValues={{}}
                    onChange={() => {}}
                    disabled
                  />
                )}
                {!previewLoading && !previewTemplate?.definition && composeModality && (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    {t("app.carePlans.noTemplateForModality")}
                  </p>
                )}
                {XML_MODALITIES.includes((composeModality || "").toLowerCase()) && (
                  <PilotBanner>{t("app.carePlans.pilotHint")}</PilotBanner>
                )}
              </div>
            )}

            {selected && (
              <>
                <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h2 className="font-medium text-lg truncate">{title || selected.title}</h2>
                      {selected.client_name && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {t("app.carePlans.patient")}: {selected.client_name}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      <Badge variant={statusBadgeVariant(status)} className="capitalize">
                        {statusLabel(status)}
                      </Badge>
                      {selected.client_id && (
                        <>
                          <Button variant="outline" size="sm" className="rounded-full h-7 text-xs" asChild>
                            <Link to={`/app/clients?open=${selected.client_id}`}>
                              <ExternalLink className="h-3 w-3 mr-1" />
                              {t("app.carePlans.openClient")}
                            </Link>
                          </Button>
                          <Button variant="outline" size="sm" className="rounded-full h-7 text-xs" asChild>
                            <Link to={`/app/notes?client=${selected.client_id}`}>
                              <FileText className="h-3 w-3 mr-1" />
                              {t("app.carePlans.openNotes")}
                            </Link>
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3 items-end pt-1">
                    <div className="flex-1 min-w-[180px]">
                      <Label className="text-xs">{t("app.carePlans.titleLabel")}</Label>
                      <Input
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="mt-1 h-9"
                        disabled={isSigned}
                      />
                    </div>
                    <div className="w-44">
                      <Label className="text-xs">{t("app.carePlans.modalityLabel")}</Label>
                      <Select value={modality} onValueChange={changeModality} disabled={isSigned}>
                        <SelectTrigger className="mt-1 h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {modalities.map((m) => (
                            <SelectItem key={m.value} value={m.value}>
                              {m.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {!isSigned && (
                      <Button type="button" size="sm" className="rounded-full" onClick={saveMeta}>
                        {t("app.carePlans.saveTitle")}
                      </Button>
                    )}
                  </div>
                  {editorTemplates.length > 0 && (
                    <div>
                      <Label className="text-xs">{t("app.carePlans.selectTemplate")}</Label>
                      <Select
                        value={selected.template_id || selected.body?.template_id || editorTemplates[0]?.id || ""}
                        onValueChange={changeTemplate}
                        disabled={isSigned}
                      >
                        <SelectTrigger className="mt-1 h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          {editorTemplates.map((tpl) => (
                            <SelectItem key={tpl.id} value={tpl.id}>
                              {tpl.name}
                              {!tpl.is_system ? " (cabinet)" : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {editorTemplates.length} {t("app.carePlans.templatesAvailable")}
                      </p>
                    </div>
                  )}
                </div>

                {isSpeech && (
                  <div className="rounded-xl border border-border p-4 space-y-3 bg-card">
                    <p className="text-sm font-medium flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      {t("app.carePlans.speechSection")}
                    </p>
                    <div className="grid sm:grid-cols-3 gap-2">
                      <Input
                        placeholder={t("app.carePlans.metricPlaceholder")}
                        value={spForm.metric}
                        onChange={(e) => setSpForm({ ...spForm, metric: e.target.value })}
                      />
                      <Input
                        placeholder={t("app.carePlans.valuePlaceholder")}
                        value={spForm.value}
                        onChange={(e) => setSpForm({ ...spForm, value: e.target.value })}
                      />
                      <Input
                        placeholder={t("app.carePlans.notesPlaceholder")}
                        value={spForm.notes}
                        onChange={(e) => setSpForm({ ...spForm, notes: e.target.value })}
                      />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" size="sm" className="rounded-full" onClick={addSpeechProgress}>
                        {t("app.carePlans.addEntry")}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="rounded-full"
                        onClick={() => downloadSpeech("pdf")}
                      >
                        <Download className="h-3.5 w-3.5 mr-1" />
                        {t("app.carePlans.reportPdf")}
                      </Button>
                    </div>
                    <ul className="text-xs max-h-28 overflow-y-auto space-y-1 border rounded-lg p-2 bg-muted/30">
                      {speech.map((s) => (
                        <li key={s.id}>
                          {s.recorded_at?.slice(0, 16)} — <strong>{s.metric}</strong>: {s.value}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {templateDef ? (
                  <CarePlanFormRenderer
                    key={`${selected.id}-${modality}-${selected.template_id || ""}`}
                    definition={templateDef}
                    fieldValues={fieldValues}
                    onChange={setFieldValues}
                    disabled={isSigned}
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">{t("app.carePlans.loadingTemplate")}</p>
                )}

                {showXmlPilot && (
                  <PilotBanner>{t("app.carePlans.pilotHint")}</PilotBanner>
                )}

                <div className="flex flex-wrap gap-2 sticky bottom-0 bg-background/95 py-3 border-t border-border">
                  {!isSigned && (
                    <Button type="button" className="rounded-full" onClick={saveBody}>
                      {t("app.carePlans.savePlan")}
                    </Button>
                  )}
                  {!isSigned && (
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-full"
                      onClick={() => setSignOpen(true)}
                    >
                      <PenLine className="h-4 w-4 mr-1" />
                      {t("app.carePlans.sign")}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                    onClick={() => exportPlan("pdf")}
                  >
                    <Download className="h-3.5 w-3.5 mr-1" />
                    {t("app.carePlans.exportPdfPatient")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                    onClick={() => exportPlan("xml", "dosar")}
                  >
                    {t("app.carePlans.exportXmlDosar")}
                  </Button>
                  {["physio", "psych", "kineto"].includes((modality || "").toLowerCase()) && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                      onClick={() => exportPlan("xml", "cnas")}
                    >
                      {t("app.carePlans.exportXmlCnas")}
                    </Button>
                  )}
                  {["speech", "logopedie", "pis", "home_care"].includes((modality || "").toLowerCase()) && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                      onClick={() => exportPlan("xml", "dgaspc")}
                    >
                      {t("app.carePlans.exportXmlDgaspc")}
                    </Button>
                  )}
                  {isPis && isSigned && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="rounded-full"
                      onClick={openHrAnnexDialog}
                    >
                      {t("app.carePlans.hrAnnex")}
                    </Button>
                  )}
                </div>

                {isSigned && (
                  <p className="text-xs text-muted-foreground">
                    {t("app.carePlans.signedAt", {
                      date: formatDateTime(selected.signed_at),
                    })}
                  </p>
                )}
              </>
            )}
          </section>
        </div>

        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("app.carePlans.generateDialogTitle")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>{t("app.carePlans.clientLabel")}</Label>
                <Select
                  value={newForm.client_id}
                  onValueChange={(v) => setNewForm({ ...newForm, client_id: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={t("app.carePlans.selectClient")} />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("app.carePlans.planTitleLabel")}</Label>
                <Input
                  className="mt-1"
                  value={newForm.title}
                  onChange={(e) => setNewForm({ ...newForm, title: e.target.value })}
                />
              </div>
              <div>
                <Label>{t("app.carePlans.categoryLabel")}</Label>
                <Select
                  value={newForm.modality}
                  onValueChange={(v) => {
                    setNewForm({ ...newForm, modality: v, template_id: "" })
                    loadTemplates(v)
                  }}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {modalities.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("app.carePlans.templateLabel")}</Label>
                <Select
                  value={newForm.template_id || tplOptions.find((x) => x.is_system)?.id || ""}
                  onValueChange={(v) => setNewForm({ ...newForm, template_id: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={t("app.carePlans.systemTemplate")} />
                  </SelectTrigger>
                  <SelectContent>
                    {tplOptions.map((tpl) => (
                      <SelectItem key={tpl.id} value={tpl.id}>
                        {tpl.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button className="rounded-full w-full" onClick={createPlan}>
                {t("app.carePlans.generatePrefill")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={signOpen} onOpenChange={setSignOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("app.carePlans.signDialogTitle")}</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-muted-foreground mb-2">{t("app.carePlans.signGdprHint")}</p>
            <SignaturePad onChange={setSignatureData} />
            <Button className="rounded-full w-full mt-3" disabled={!signatureData} onClick={signPlan}>
              {t("app.carePlans.confirmSign")}
            </Button>
          </DialogContent>
        </Dialog>

        <Dialog open={hrAnnexOpen} onOpenChange={setHrAnnexOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("app.carePlans.hrAnnexDialogTitle")}</DialogTitle>
            </DialogHeader>
            {hrEmployees.length === 0 ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">{t("app.carePlans.hrAnnexEmpty")}</p>
                <Button variant="outline" className="rounded-full w-full" asChild>
                  <Link to="/app/hr/employees">{t("app.carePlans.hrAnnexGoHr")}</Link>
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <Label>{t("app.carePlans.hrAnnexPickEmployee")}</Label>
                  <Select value={hrEmployeeId} onValueChange={setHrEmployeeId}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder={t("app.carePlans.hrAnnexPickEmployee")} />
                    </SelectTrigger>
                    <SelectContent>
                      {hrEmployees.map((e) => (
                        <SelectItem key={e.id} value={e.id}>
                          {`${e.first_name || ""} ${e.last_name || ""}`.trim() || e.id.slice(0, 8)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  className="rounded-full w-full"
                  disabled={!hrEmployeeId || hrAnnexLoading}
                  onClick={generateHrAnnex}
                >
                  {t("app.carePlans.hrAnnexConfirm")}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </AppLayout>
    </FeatureGate>
  )
}
